/** The siege works: courses of bevelled blocks, gate, towers, keep, their
 * shadows and the livery they fly. Split out of `arena.ts` on 2026-09-17;
 * the layer model and the reasons behind the drawn (not stamped) masonry are
 * in that file's header. Every function here is free of `Arena` state and
 * takes the `Graphics` it paints into.
 */

import { Container, Sprite } from "pixi.js";
import type { Graphics } from "pixi.js";
import type { BattleState, Obstacle } from "@game/core";
import { BF_HEIGHT, BF_WIDTH, SIDE_DEFENDER, hexCol, hexIndex, hexRow } from "@game/core";
import type { AtlasFrame, AtlasLike } from "../deps.js";
import { COLOR, mixColor, noise } from "../theme.js";
import { HEX_W, asHex, hexCentre } from "../grid.js";
import { siegeKey } from "../keys.js";

/**
 * The ashlar. 13x21 arena px was 20x32 device px at 1080p - a block the size
 * of a creature's head, and a critic read the works as "a tile-set fort". On
 * A reference screenshot a course of the curtain is 4-5 rows of a 556-row frame
 * (5-6 arena rows here) and a block a little under twice as long as it is
 * high; 8x13 keeps the same proportion at a size that reads as coursed stone
 * rather than as bricks laid one to a hand.
 */
const BLOCK_H = 8;
const BLOCK_W = 13;
const MORTAR = 1.2;

/**
 * The two ends of the light on a stone face, as **temperature** rather than as
 * value.
 *
 * Measured off a reference screenshot,
 * The expansion's curtain is warm the whole way through - the sunlit courses run
 * (155,124,88) at the median and (255,235,184) at the 95th percentile, R above
 * G above B at every level - and what separates a lit face from a shaded one
 * there is not only that the shade is darker but that it is *bluer*, because
 * the only light reaching it is sky. Ours measured (73,65,53) lit against
 * (69,61,50) unlit on the shipped 1080p siege shot: a R-B spread of +20.6
 * against +19.1, which is 1.5 levels of difference between the two faces of the
 * biggest object on the screen. A wall with no temperature in it is a grey
 * elevation drawing of a wall.
 */
const STONE_SUN = 0xf2dda6;
const STONE_WARM = 0xc9a869;
const STONE_SKY = 0x445468;
const STONE_SKYDARK = 0x0f141c;

/**
 * A faction's livery: what the defender's own works are painted and flown in.
 *
 * The siege screenshot had nothing on it that said who was being besieged.
 * Greenmantle defends the fixture and eleven bays of neutral grey masonry say as
 * much about that as a wall in a screenshot of any other game does. The classic puts
 * the answer on the roofs and on the cloth: the roof colour and the pennants
 * are the town's, and they are the first thing that reads at arm's length.
 *
 * The defending faction is taken from the defender's own stacks rather than
 * threaded in as an option, because `BattleState` already carries it -
 * `stacks.def` is a content id whose prefix is the faction - and an option
 * would have to be passed by `presenter.ts`, which would then be free to
 * disagree with the army standing on the board.
 */
interface Livery {
  /** The cloth: banners on the curtain, the pennant over each turret. */
  readonly field: number;
  /** The metal on the cloth and on the gate's shield. */
  readonly trim: number;
  /** The tile on a turret roof. */
  readonly roof: number;
}

const FACTION_LIVERY: Readonly<Record<string, Livery>> = {
  sunspire: { field: 0x2f5bc4, trim: 0xe8cf7a, roof: 0x2c3f7a },
  greenmantle: { field: 0x2f7a34, trim: 0xe8cf7a, roof: 0x2f5a2b },
  skyreach: { field: 0x3f7fa8, trim: 0xe8e2c0, roof: 0x2b5a72 },
  emberpit: { field: 0xa8321c, trim: 0xe0a03a, roof: 0x5c1c10 },
  gravewatch: { field: 0x4a4a56, trim: 0xa8b0a0, roof: 0x2a2a32 },
  deepvault: { field: 0x6a2f8a, trim: 0xd8c07a, roof: 0x3a1c4a },
  ironhorde: { field: 0xb4531c, trim: 0xe4c07a, roof: 0x6a3212 },
  fenmire: { field: 0x5a6a2a, trim: 0xc8b878, roof: 0x3a4418 },
  prismgate: { field: 0xc8a020, trim: 0xfff0c0, roof: 0x7a5c10 },
  saltreach: { field: 0x2a7a72, trim: 0xe8cf7a, roof: 0x174a46 },
  cogworks: { field: 0x8a6a3a, trim: 0xd8c898, roof: 0x4a3a20 },
  frostmark: { field: 0x2f6a8a, trim: 0xdcd0a8, roof: 0x1c4258 },
  blackwater: { field: 0x1a4a48, trim: 0xc8c050, roof: 0x163a32 },
  neutral: { field: 0x6a6152, trim: 0xc0b494, roof: 0x3a352c },
};

export const liveryFor = (faction: string): Livery =>
  FACTION_LIVERY[faction] ?? FACTION_LIVERY["neutral"]!;

/** Damage states, mirroring `Obstacle.integrity`. */
export const WALL_DESTROYED = 0;
export const WALL_DAMAGED = 1;
export const WALL_INTACT = 2;

/**
 * How wide a bay of curtain is drawn, as a multiple of a cell. It has to close
 * the half-cell stagger between odd and even rows; past ~1.35 the bays swallow
 * each other and the run reads as one slab.
 */
export const BAY_WIDEN = 1.3;

/**
 * The three heights, at front-row scale.
 *
 * A run is drawn back to front, so the bay on row r+1 covers the bay on row r
 * from `ROW_PITCH - height(r+1)` above its ground line downward: the slice of
 * any bay a player ever sees is exactly one row pitch, whatever the height is.
 * The height therefore has one job - be greater than a pitch at the *smallest*
 * scale on the run - and every pixel past that is buried. `CURTAIN_H` used to
 * be 84 at a flat scale; at `SIEGE_SCALE_FAR` the shortest covering bay (row 1)
 * now stands 53.5, which clears `ROW_PITCH` = 42 by eleven pixels.
 *
 * The turret and the gatehouse are the two pieces that must stand clear of the
 * bay in front and be read whole, and both came down: at 104 and 150 they were
 * portrait blocks two thirds as wide as they were tall, and three of them in a
 * column is the "three identical gatehouse blocks" a blind review named.
 */
// Scaled 50/42 with the row pitch (76, 84 and 98 before), so the works keep
// their height against the creatures that assault them.
export const CURTAIN_H = 90;
export const TOWER_H = 100;
export const GATE_H = 117;
/**
 * The keep: taller than a flanking turret and drawn in the right margin on
 * the ground line of `KEEP_ROW`. Row 6 rather than 5 so the roof's apex stays
 * clear of the defender's commander, who stands level with the second rank
 * in the same margin; at 120 the apex lands about 80 rows under his feet.
 */
/**
 * **A donjon, not a turret.** The keep was `paintTower` at 70x120: a plain
 * cylinder with a cone on it, parked in the margin, and a critic named it "an
 * isolated plain cylinder keep". The classic's citadel (a reference screenshot,
 * a reference screenshot) is a big square building at the right edge, cut
 * by the frame, with a lower wing toward the field: `paintKeep` draws that,
 * and `KEEP_UNDER_FRAME` is how far its right side runs under the leather so
 * it reads as a building the frame crops rather than a prop that fits.
 */
export const KEEP_H = 168;
export const KEEP_W = 104;
/**
 * Row 8, not 7, and 220 arena px for the painted donjon: the classic's keep stands in
 * the lower half of the frame with its base near the front ranks
 * (a reference screenshot x1560-1690 y270-820, base at 76% of the
 * frame height; a reference screenshot right edge, cut by the frame). At row 7
 * and 190 it was a 154x261 device px object floating at mid-height.
 */
export const KEEP_ROW = 8;
export const KEEP_UNDER_FRAME = 26;

/**
 * The two painted halves stacked into one keep, in the source's own space.
 *
 * The packer trims each half to its ink, and the halves' ink is not the same
 * width (the base splays, the turret leans): stacking them by their anchors
 * put a jog at the waist. Each is placed by its `trimX`/`trimY` inside its
 * untrimmed source box, and the two boxes share one width and one axis, so
 * the joint is the painting's own row. The top half carries one row of
 * overlap (`build_siege.py` KEEP_OVERLAP); it is drawn OVER the bottom half
 * so a rounding gap can only close. Origin: bottom-centre on the ground line.
 */
export function keepStack(
  atlas: AtlasLike, height: number,
): { readonly holder: Container; readonly w: number; readonly h: number } | null {
  const top = atlas.frame(siegeKey("keep_top"));
  const bottom = atlas.frame(siegeKey("keep_bottom"));
  if (top == null || bottom == null || top.srcH === 0 || bottom.srcH === 0) return null;
  const srcH = top.srcH - KEEP_OVERLAP + bottom.srcH;
  const srcW = Math.max(top.srcW, bottom.srcW);
  const k = height / srcH;
  const holder = new Container();
  holder.label = "arena.keep";
  const place = (frame: AtlasFrame, key: string, yTop: number): Sprite => {
    const sp = new Sprite(atlas.texture(key));
    sp.anchor.set(0, 0);
    sp.scale.set(k);
    sp.position.set((-srcW / 2 + frame.trimX) * k, yTop + frame.trimY * k);
    return sp;
  };
  const b = place(bottom, siegeKey("keep_bottom"), -bottom.srcH * k);
  const t = place(top, siegeKey("keep_top"), -srcH * k);
  holder.addChild(b, t);
  return { holder, w: srcW * k, h: srcH * k };
}

/**
 * The painted sheet's sizes, in arena px at front-row scale.
 *
 * A bay window is 120 texels for an 80px bay (`build_siege.py`), so the
 * stamp is a hair wider than the procedural bay (`BAY_ART_WIDEN`) to close
 * the half-cell stagger the same way `BAY_WIDEN` did. The turret and the
 * gatehouse are sized by height: the classic's gatehouse stands about a third taller
 * than a bay and its turrets half again (a reference screenshot), and the
 * keep is the tallest thing on the screen.
 */
export const BAY_ART_WIDEN = 1.04;
export const TURRET_ART_H = 170;
/**
 * The open drum on the curtain, in arena px at front-row scale.
 *
 * The sheet carries the tower's HEAD, not the whole tower (`build_siege.py`
 * DRUM_HEAD): 120x103 texels, so 74 arena px of height is 86 of width - five
 * px wider than the 81-px ribbon it stands in, which is the proportion
 * A reference screenshot draws (a drum 37 source px across a curtain of ~28).
 *
 * **74, not 106, and the 106 is why a blind critic called these washers.**
 * Measured on that reference (`campaign/.../crops/ref_drum_grid.png`, the
 * curtain's top tower at src x578..615): the ring is 37x22 source px on a
 * 42-px row pitch, and it stands 0.88 of a pitch clear of the crest of the
 * curtain beside it. At 106 ours drew a ring 65 arena px tall - 1.3 pitches
 * of open cylinder mouth, twice the reference's - so the two towers, not the
 * wall, were the largest objects on that side of the frame, and what the eye
 * had most of to read was the inside of them.
 *
 * The foot is cut, and the cut is covered: the bay one row in front stands
 * 81 arena px over its own ground line, 50 below this one, so it covers the
 * bottom 31 px of anything standing here. 74 - 31 leaves 43 arena px of tower
 * clear of the curtain, 0.86 of a row pitch, which is the reference's 0.88.
 */
export const DRUM_ART_H = 74;
/**
 * How far a tower stands out toward the attacker from the wall line, in arena
 * px. A reference screenshot's drums bulge past the curtain on the moat side
 * by about a third of their own width; the wall passes behind them.
 */
export const TOWER_PROJECTION = 20;
/**
 * The rows that carry a scenery turret, and how tall it is drawn.
 *
 * Between the fighting towers on rows 1 and 10 and the gate on row 5, so the
 * run reads tower - tower - gate - tower - tower down the board: the two to
 * three row spacing a reference screenshot uses down its own curtain. They are
 * drawn at `DRUM_ART_H`, the same size as the two the rules shoot from: the classic
 * makes no visual distinction between them either.
 */
export const RUN_TURRET_ROWS: readonly number[] = [3, 8];
/**
 * The gatehouse head (`build_siege.py` GATE_HEAD_ROWS): its arch lands on the
 * ground line and its turrets stand ~40px over the crest of the bays either
 * side, which is the gate-to-bay proportion of a reference screenshot.
 */
export const GATE_ART_H = 168;
/**
 * 340 arena px at the keep's row: 6.8 row pitches. The classic's keep is the tallest
 * thing on the screen - a reference screenshot x1560-1690 y270-820 is
 * 550 device px at a 76px pitch (7.2 pitches), a reference screenshot
 * about 6, and a reference screenshot runs its keep from y=180 to the bottom
 * of a 556px frame. At 220 (4.4 pitches) a critic read "a miniature". The
 * sheet carries it as two halves (`keepStack`) so this height is drawn at
 * the bays' own texel density rather than as a blow-up.
 */
export const KEEP_ART_H = 340;
/** One row: see `build_siege.py` KEEP_OVERLAP. */
const KEEP_OVERLAP = 1;
/**
 * See `buildKeep`: the keep's own stone, a quarter stop under the bays'.
 *
 * Cooled from 0xc9c2b6 when the curtain was repainted. `sg_keep_tower` is a
 * warm cream donjon and the new ribbon (`build_siege.py` RUN_FLOOR/RUN_CEIL)
 * is grey-green limestone; side by side on the 2026-09-07 shot the keep read
 * as a building from another town. The tint multiplies, so taking red down
 * and green up walks the keep's stone onto the curtain's without touching
 * its modelling.
 */
export const KEEP_TINT = 0xb7c0b4;

/**
 * How much wider than a curtain bay a turret is drawn. It must be > 1: a turret
 * narrower than the wall it stands in leaves a vertical step at the joint, and
 * what shows through that step is the unlit side face of the bay behind.
 */
export const TOWER_WIDEN = 1.14;

/**
 * The plumb line a column of masonry is built on: the mean of the even-row and
 * odd-row cell centres in that column, so the wall stands between the two
 * staggered halves of its own column rather than lurching between them.
 */
export function wallLineX(hex: number): number {
  return hexCentre(asHex(hexIndex(hexCol(hex), 0))).x + HEX_W * 0.25;
}

/**
 * Which window of the painted run each bay stamps: see `SiegePiece.window`.
 * Exported for the test that holds the joint rule.
 */
export function siegeWindows(obstacles: readonly Obstacle[]): Map<number, number> {
  const kinds = new Map<number, Obstacle["kind"]>();
  for (const o of obstacles) {
    if (o.kind === "wall" || o.kind === "gate" || o.kind === "tower") kinds.set(o.hex, o.kind);
  }
  const out = new Map<number, number>();
  for (const [hex, kind] of kinds) {
    if (kind !== "wall") continue;
    let n = 0;
    let above = hex - BF_WIDTH;
    while (kinds.get(above) === "wall") { n++; above -= BF_WIDTH; }
    // The run after the gate: the first bay shows its foot, not its crest
    // (the gatehouse is sorted in front and covers it), so the next bay
    // skips a window rather than repeating the same stones.
    const afterGate = kinds.get(above) === "gate";
    out.set(hex, afterGate && n > 0 ? n + 1 : n);
  }
  return out;
}

// ------------------------------------------------------- the depth axis

/**
 * A curtain wall is built along a **depth axis**, not down a column.
 *
 * Eleven bays drawn on one plumb line at one scale is an elevation drawing - a
 * facade pasted on the ground plane - and that is what three blind reviews in a
 * row said this screen was. What separates the classic's fortification from ours was
 * never the surface of the stone; it was that theirs *recedes*.
 *
 * Two things do the receding, and both are read off the reference shots rather
 * than invented:
 *
 *   - **Foreshortening.** On a reference screenshot
 *     and on the expansion empty field the same bay of curtain is a little over half
 *     as tall at the back of the board as at the front. The board's own prop
 *     ramp (`hexRowScale`, 0.90..1.00) is a 1.11 ratio, which is a rounding
 *     error at this size, and a blind review measuring the shipped shot read
 *     the per-block scale down the run as 1.00. The works get their own ramp,
 *     `SIEGE_SCALE_FAR..SIEGE_SCALE_NEAR`, a ratio of 1.67.
 *
 *   - **A wall line that is a curve.** A town is round, so the curtain the
 *     besieger faces is an arc: it bulges toward him at the gate and pulls back
 *     to the corners. Masking the lava in the ditch on
 *     a reference screenshot puts its inner edge at
 *     x=502 at the back of the field, 435 at the waist and 498 at the front of
 *     an 800px frame: a 67px swing, 8.4% of the frame's width. `SIEGE_LEAN`
 *     and `SIEGE_BOW` together give 28 arena px across our own run, a little
 *     under half of that, because the lattice under it is rectangular and a bay
 *     displaced further than half a cell stops standing on the cell whose rules
 *     it enforces.
 *
 * Nothing here touches `hexCentre`, `hexPolygon` or anything the pointer is
 * tested against. It moves *paint*, and only along the run.
 */
// 0.88..1.06 (ratio 1.2), down from 0.78..1.06 (1.36) and 0.66..1.10 (1.67):
// the two end turrets are the same painting, and at 1.36 the front one still
// drew 180 arena px against the back one's 138 on the 1080p shot - a fat
// drum at the foot and a spire at the head, which a critic listed as two
// different towers. 1.2 is a reference screenshot's own ratio (top turret
// ~40px wide, bottom ~48px); the HD dungeon siege's end towers measure 230
// and 220 rows, no ramp at all, so this is the most perspective the classic ever
// draws on a wall. The bow and the lean carry the run's depth from here.
export const SIEGE_SCALE_FAR = 0.88;
export const SIEGE_SCALE_NEAR = 1.06;

/** How far the back of the run leans in toward the town, in arena px. */
export const SIEGE_LEAN = 14;
/**
 * How far the waist of the run bulges out toward the attacker, in arena px.
 * 36 with the lean at 14 is a 43px swing, 3.4% of the frame (24/24 was 37px
 * and a critic still read a plumb column) - still under the classic's 8.4%, and as far
 * as the run can go while every bay stays within half a cell of the column it
 * enforces (`battle-siege-projection.spec.ts`): the waist bay stands 29px, 0.48
 * of a cell, toward the attacker off its own rule cell. The rules put the
 * whole run in one column (`move.ts`: walls are a vertical run), so the classic's
 * diagonal, whose wall hexes change column, is not available to the paint.
 */
export const SIEGE_BOW = 36;

/** Where a row sits on the depth axis: 0 at the back of the board, 1 at the front. */
function siegeDepth(row: number): number {
  return BF_HEIGHT <= 1 ? 1 : row / (BF_HEIGHT - 1);
}

/**
 * Aerial perspective on the run: the far end cooler and flatter than the near.
 *
 * The interpolant is the row's **place on the depth axis**, which is bounded to
 * 0..1 by construction, and not the row's scale. `mixColor` extrapolates rather
 * than clamping, and `SIEGE_SCALE_NEAR` is greater than 1: fed `s * s` the front
 * row came out at (268, 257, 237), pixi refused it with "Unable to convert color
 * 17629677", and the battle renderer failed to mount with nothing on screen but
 * the moat. A colour that leaves the cube is not a colour, and every caller of
 * `mixColor` in this file has to own the bound on its own `t`.
 */
export function siegeTint(row: number): number {
  const d = siegeDepth(row);
  return mixColor(0xc2c0ba, 0xfff6e4, d * d);
}

/** How big masonry standing on `row` is drawn, relative to a front-row bay. */
export function siegeScale(row: number): number {
  const t = siegeDepth(row);
  return SIEGE_SCALE_FAR + (SIEGE_SCALE_NEAR - SIEGE_SCALE_FAR) * t;
}

/**
 * The wall line's own x at `row`: the plumb line, leaned back at the far end and
 * bowed out at the waist. `bulge` is 0 at both corners and 1 in the middle.
 */
export function siegeStationX(hex: number): number {
  const t = siegeDepth(hexRow(hex));
  const bulge = 1 - 4 * (t - 0.5) * (t - 0.5);
  return wallLineX(hex) + SIEGE_LEAN * (1 - t) - SIEGE_BOW * bulge;
}

/** How far the gatehouse stands out from the curtain, toward the attacker. */
export const GATE_PROJECTION = 13;

/**
 * The oblique the rest of the game is drawn in, as the offset from a vertical
 * face to the top plane behind it, per pixel of the plane's own depth.
 *
 * The whole fortification had not one horizontal surface on it - every plane in
 * it faced the camera - which is the other half of "an elevation, not a
 * building". A merlon seen from in front and above shows its cap; the wall walk
 * behind the parapet shows through the crenels; the footing shows its splay.
 * Those three planes are the ones that say a wall has a thickness.
 */
export const OBLIQUE_X = 0.62;
export const OBLIQUE_Y = -0.42;

// --------------------------------------------------------------- masonry

/**
 * One course of blocks, lit from the upper left. `y` is the course's top edge in
 * the graphic's own space, which grows upward from the ground line at y = 0.
 */
function course(
  g: Graphics, x0: number, y: number, w: number, seed: number, row: number, offset: number,
  faceX0 = x0, faceW = w,
): void {
  let x = x0 - offset;
  let col = 0;
  while (x < x0 + w) {
    const bw = Math.min(BLOCK_W, x0 + w - x);
    const draw = Math.max(0, bw - MORTAR);
    if (x + bw > x0 && draw > 1) {
      const left = Math.max(x0, x);
      const width = Math.min(x + draw, x0 + w) - left;
      if (width > 1) {
        // A wide tonal spread block to block. At 0.15..0.55 between two values
        // that are themselves close, every block came out the same mid grey and
        // 500px of wall was one flat column; masonry is legible because no two
        // stones in a course are quite the same stone.
        const v = noise(seed, row * 31 + col * 7);
        // Where this block sits across the BAY - not across the slice it is
        // being drawn in. `paintWall` clips a course into `BLOCK_W` pieces, so
        // the slice's own `x0` is meaningless as a light coordinate and the
        // face has to be passed in.
        const u = faceW <= 0 ? 0.5 : Math.min(1, Math.max(0, (left + width / 2 - faceX0) / faceW));
        // Started at 0.3..0.85 of the way to `stoneShadow`, which puts the
        // whole face in the bottom third of the value range whatever the light
        // is doing. The expansion's curtain sits at a median of (155,124,88) and it is
        // the *value* of the stone, not its shading, that carries most of that.
        let base = mixColor(COLOR.stoneLit, COLOR.stoneShadow, 0.12 + v * 0.46);
        // Sun on the left of the face, sky on the right. This is the whole of
        // the temperature split: the same value can be quarried limestone in
        // the sun or the same stone lit only by sky, and only the hue says
        // which. See STONE_SUN.
        base = mixColor(base, STONE_WARM, 0.34 * (1 - u));
        base = mixColor(base, STONE_SKY, 0.34 * u);
        g.rect(left, y, width, BLOCK_H - MORTAR).fill({ color: base });
        g.rect(left, y, width, 1.1).fill({ color: STONE_SUN, alpha: 0.62 - u * 0.34 });
        g.rect(left, y + BLOCK_H - MORTAR - 1.1, width, 1.1)
          .fill({ color: mixColor(0x140e07, STONE_SKYDARK, u), alpha: 0.55 });
        g.rect(left, y, 1.1, BLOCK_H - MORTAR).fill({ color: STONE_SUN, alpha: 0.34 - u * 0.2 });
        g.rect(left + width - 1, y, 1, BLOCK_H - MORTAR)
          .fill({ color: mixColor(0x140e07, STONE_SKYDARK, u), alpha: 0.4 + u * 0.18 });
      }
    }
    x += BLOCK_W;
    col++;
  }
}

/**
 * How tall the wall still stands at horizontal position `u` in 0..1.
 *
 * The floor matters as much as the shape. Only the top pitch of a bay is ever
 * on screen, so a damaged bay knocked down to 0.42 of its height sat entirely
 * inside the part the bay in front covers, and a catapult hit changed nothing a
 * player could see. A damaged bay now keeps its jambs and loses a bite out of
 * the middle down to about two thirds - which is inside the visible slice, and
 * which also costs it its crenellations, so the step it makes in the run's
 * silhouette is missing as well.
 */
function damageProfile(integrity: number, u: number, seed: number): number {
  if (integrity >= WALL_INTACT) return 1;
  if (integrity <= WALL_DESTROYED) return 0.62 + noise(seed, Math.floor(u * 8)) * 0.1;
  const bite = 1 - Math.exp(-((u - 0.5) * (u - 0.5)) / 0.055);
  return Math.min(1, 0.62 + bite * 0.4 + noise(seed, Math.floor(u * 12)) * 0.1);
}

export function paintWall(
  g: Graphics, w: number, height: number, integrity: number, seed: number, livery?: Livery,
): void {
  const x0 = -w / 2;
  if (integrity <= WALL_DESTROYED) { paintBreach(g, x0, w, height, seed); paintWallBase(g, x0, w, seed); return; }
  const courses = Math.max(1, Math.round(height / BLOCK_H));
  // A buttress on every other bay, projecting past the wall line into the ditch.
  // It is the one part of a bay that is *not* covered by the bay in front of it
  // over its whole height, so down the run these are a rhythm of vertical
  // accents against what was otherwise 850px of unbroken vertical edge.
  if ((seed & 1) === 0) paintButtress(g, x0, height);
  for (let r = 0; r < courses; r++) {
    const y = -(r + 1) * BLOCK_H;
    const frac = (r + 1) / courses;
    // Clip the course to the surviving profile.
    let x = x0;
    const step = BLOCK_W;
    while (x < x0 + w) {
      const u = (x - x0 + step / 2) / w;
      if (damageProfile(integrity, u, seed) >= frac) {
        const cw = Math.min(step, x0 + w - x);
        bed(g, x, y, cw);
        course(g, x, y, cw, seed, r, (r & 1) === 1 ? BLOCK_W / 2 : 0, x0, w);
      }
      x += step;
    }
  }
  paintQuoins(g, x0, w, courses * BLOCK_H, seed);
  if (integrity >= WALL_INTACT) {
    paintCrenellations(g, x0, -courses * BLOCK_H, w, seed);
  } else {
    // A damaged bay has lost its parapet, and the top course it is left with is
    // broken rather than cut: a straight top edge at a lower height reads as a
    // *shorter wall*, not as a damaged one.
    paintBrokenTop(g, x0, w, courses * BLOCK_H, integrity, seed);
  }
  paintWeathering(g, x0, w, courses * BLOCK_H, seed);
  paintFaceRamp(g, x0, w, courses * BLOCK_H);
  if (integrity <= WALL_DAMAGED) paintRubble(g, x0, w, seed);
  paintWallBase(g, x0, w, seed);
  // Not every bay: a banner on every one of eleven is bunting. One in three,
  // decided by the bay's own seed, so the run has a rhythm and the same bay
  // always carries it.
  if (livery !== undefined && integrity >= WALL_INTACT && seed % 3 === 1) {
    paintBanner(g, 0, -courses * BLOCK_H + 4, livery, seed);
  }
}

/**
 * What a hundred years of weather leaves on a wall face.
 *
 * The blind read on the shipped shot was "uniform mid-grey: no weathering, no
 * wear", and it is the truest of the three because it is the one that cannot be
 * fixed by moving anything. Every block already carries its own value, but a
 * value scatter at block scale is *noise*, and noise at one frequency reads as
 * a texture swatch. What makes a real wall legible is that it is dirty at a
 * scale much larger than its stones: rain runs off the parapet in the same
 * places for a century, damp climbs the first three courses, the head bleaches
 * where the sun reaches it, and blocks spall at the corners.
 *
 * All of it is laid UNDER the face ramp, so the ambient shade at the foot
 * integrates the staining rather than sitting on top of it.
 */
function paintWeathering(g: Graphics, x0: number, w: number, height: number, seed: number): void {
  if (height <= 0 || w <= 0) return;
  // 1. Wash off the parapet. A gutter drips in the same place for a century, so
  //    the streak is long, narrow and repeated rather than a general grime.
  for (let i = 0; i < 9; i++) {
    const t = noise(seed, i * 5 + 1);
    const x = x0 + 2 + t * (w - 6);
    const len = height * (0.28 + noise(seed, i * 5 + 2) * 0.62);
    const sw = 1.4 + noise(seed, i * 5 + 3) * 3.8;
    g.rect(x, -height + 2, sw, len)
      .fill({ color: 0x2c2b23, alpha: 0.16 + noise(seed, i * 5 + 4) * 0.17 });
    g.rect(x + sw, -height + 2, 1.1, len * 0.78).fill({ color: 0xefe2be, alpha: 0.08 });
  }
  // 2. Sun-bleach along the head, where the light actually lands.
  for (let i = 0; i < 5; i++) {
    g.rect(x0, -height + i * 4.5, w, 4.8)
      .fill({ color: 0xf3e6bc, alpha: 0.075 - i * 0.012 });
  }
  // 3. Damp and lichen banked up the bottom third, denser at the two corners
  //    where water stands longest.
  for (let i = 0; i < 24; i++) {
    const t = noise(seed, i * 7 + 11);
    const edge = t < 0.5 ? t * t * 2 : 1 - (1 - t) * (1 - t) * 2;
    const x = x0 + edge * w;
    const y = -noise(seed, i * 7 + 12) * height * 0.36;
    const r = 3 + noise(seed, i * 7 + 13) * 7.5;
    const damp = noise(seed, i * 7 + 14);
    g.ellipse(x, y, r, r * 0.55)
      .fill({ color: mixColor(0x3d4a22, 0x616f36, damp), alpha: 0.15 + damp * 0.22 });
  }
  // 4. Spalled blocks: a bite out of the face with a lit lip above it, which is
  //    the one kind of wear that shows as geometry rather than as a stain.
  for (let i = 0; i < 5; i++) {
    const x = x0 + 4 + noise(seed, i * 13 + 21) * (w - 12);
    const y = -8 - noise(seed, i * 13 + 22) * (height - 18);
    const cw = 5 + noise(seed, i * 13 + 23) * 8;
    const ch = 3.5 + noise(seed, i * 13 + 24) * 4;
    g.rect(x, y, cw, ch).fill({ color: 0x1a130a, alpha: 0.5 });
    g.rect(x, y, cw, 1.2).fill({ color: 0xd8c9a4, alpha: 0.45 });
  }
}

/**
 * A gonfalon hung over the parapet in the defender's colours.
 *
 * Cloth is the cheapest and loudest heraldry a fortification has, and it is the
 * only thing on the works that is allowed to be saturated - which is exactly
 * why it reads: eleven bays of stone at 20 levels of chroma, and one banner at
 * 120, and the eye finds the banner and reads the town off it. Hung from the
 * top of the courses so it falls into the one row pitch of a bay that is ever
 * on screen.
 */
function paintBanner(g: Graphics, cx: number, top: number, livery: Livery, seed: number): void {
  const bw = 15;
  const bh = 27 + noise(seed, 3) * 5;
  const x = cx - bw / 2;
  g.rect(x + 2, top - 2, bw, bh + 3).fill({ color: 0x0b0803, alpha: 0.4 });
  // The pole it hangs from, over the merlons.
  g.rect(x - 3, top - 5, bw + 6, 2.6).fill({ color: 0x2a2118 });
  g.rect(x - 3, top - 5, bw + 6, 1).fill({ color: 0x8a7a5c, alpha: 0.7 });
  // The field, with the shaded fold down its right half - a flat rectangle of
  // colour is a sticker, and the fold is what makes it cloth.
  g.poly([x, top - 3, x + bw, top - 3, x + bw, top + bh - 7, cx, top + bh, x, top + bh - 7])
    .fill({ color: livery.field });
  g.poly([cx, top - 3, x + bw, top - 3, x + bw, top + bh - 7, cx, top + bh])
    .fill({ color: 0x000000, alpha: 0.26 });
  g.rect(x, top - 3, bw, 1.6).fill({ color: mixColor(livery.field, 0xffffff, 0.5), alpha: 0.8 });
  // The metal on it: an edge and a bar, so the cloth carries a device rather
  // than being a coloured shape.
  g.rect(x, top + 6, bw, 2).fill({ color: livery.trim, alpha: 0.85 });
  g.rect(x + bw * 0.42, top + 1, bw * 0.16, bh - 12).fill({ color: livery.trim, alpha: 0.7 });
}

/**
 * Quoins: the dressed corner stones at a bay's two ends.
 *
 * Every bay in the run is the same width on the same plumb line, so with a plain
 * face the eleven of them composited into one uninterrupted field of blocks -
 * measured as one segment tiled five times, with no joint anywhere. The quoin is
 * the joint: a proud, lighter stone alternating long-and-short up both edges,
 * with a dark reveal outside it. It is the one part of a bay that is *not*
 * covered by the bay in front (it stands 2px proud of the wall line), so down
 * the run it is a vertical accent per bay rather than a single 850px edge.
 */
function paintQuoins(g: Graphics, x0: number, w: number, height: number, seed: number): void {
  const rows = Math.max(1, Math.round(height / BLOCK_H));
  for (const side of [0, 1]) {
    for (let r = 0; r < rows; r++) {
      const long = ((r + side) & 1) === 0;
      const qw = long ? 9 : 6;
      const y = -(r + 1) * BLOCK_H;
      const x = side === 0 ? x0 - 2 : x0 + w + 2 - qw;
      const v = noise(seed, r * 13 + side * 5);
      // The reveal beside a quoin is a *mortar joint*, so it is laid at 0.6 of a
      // warm dark rather than at 0.9 of a cold one: at full strength the eleven
      // bays' quoin beds line up into an unbroken 800px column of pure black
      // down the run, which is the untextured seam this pass exists to remove.
      g.rect(x - 1.5, y - 1.5, qw + 3, BLOCK_H + 1.5).fill({ color: 0x241a0e, alpha: 0.6 });
      g.rect(x, y, qw, BLOCK_H - MORTAR)
        .fill({ color: mixColor(COLOR.stoneLit, COLOR.stone, 0.18 + v * 0.3) });
      g.rect(x, y, qw, 1.2).fill({ color: 0xd3c4a2, alpha: 0.7 });
      g.rect(x, y + BLOCK_H - MORTAR - 1.2, qw, 1.2).fill({ color: 0x0f0a05, alpha: 0.6 });
    }
  }
}

/**
 * The broken top edge of a bay that has taken a hit but is still standing.
 *
 * Damage has to be readable inside the one row pitch of a bay that is ever on
 * screen, so the tell is not the height - it is the *edge*: a stepped, gap-toothed
 * course with scorch under it, where an intact bay carries an even row of merlons.
 */
function paintBrokenTop(
  g: Graphics, x0: number, w: number, height: number, integrity: number, seed: number,
): void {
  let x = x0;
  let i = 0;
  while (x < x0 + w) {
    const bw = Math.min(11, x0 + w - x);
    const u = (x - x0 + bw / 2) / w;
    const top = -height * damageProfile(integrity, u, seed);
    const drop = noise(seed, i * 7 + 3) * 7;
    // Scorch: the soot plume a hit leaves, running down the face under the break.
    g.rect(x, top + drop, bw, 16 + noise(seed, i * 7 + 5) * 14)
      .fill({ color: 0x140d06, alpha: 0.28 });
    // The fractured edge itself, lit on top the way a fresh break is.
    g.rect(x - 1, top + drop - 2.5, bw + 2, 3.5).fill({ color: 0x0d0904, alpha: 0.9 });
    g.rect(x, top + drop - 1.5, bw, 1.8).fill({ color: 0xbfae8c, alpha: 0.75 });
    x += 11;
    i++;
  }
}

export function paintGate(
  g: Graphics, w: number, height: number, integrity: number, open: boolean, seed: number,
  livery?: Livery,
): void {
  const x0 = -w / 2;
  if (integrity <= WALL_DESTROYED) { paintBreach(g, x0, w, height, seed); paintWallBase(g, x0, w, seed); return; }
  const courses = Math.max(1, Math.round(height / BLOCK_H));
  const archW = w * 0.52;
  const archH = height * 0.62;
  for (let r = 0; r < courses; r++) {
    const y = -(r + 1) * BLOCK_H;
    const frac = (r + 1) / courses;
    let x = x0;
    while (x < x0 + w) {
      const cx = x - x0 + BLOCK_W / 2 - w / 2;
      const u = (x - x0 + BLOCK_W / 2) / w;
      // Skip blocks inside the arch: a rectangle capped by a half ellipse.
      const inArch =
        integrity > WALL_DESTROYED
        && Math.abs(cx) < archW / 2
        && (r + 1) * BLOCK_H < archH * (1 - Math.pow((2 * cx) / archW, 4) * 0.35);
      if (!inArch && damageProfile(integrity, u, seed) >= frac) {
        const cw = Math.min(BLOCK_W, x0 + w - x);
        bed(g, x, y, cw);
        course(g, x, y, cw, seed, r, (r & 1) === 1 ? BLOCK_W / 2 : 0, x0, w);
      }
      x += BLOCK_W;
    }
  }
  if (integrity >= WALL_INTACT) {
    // A corbelled head that oversails the curtain either side of it. A gatehouse
    // is the one part of a curtain wall that projects, and without the oversail
    // the gate read as a taller stretch of the same wall with a hole in it.
    const top = -courses * BLOCK_H;
    g.rect(x0 - 6, top - 7, w + 12, 7).fill({ color: COLOR.stone });
    g.rect(x0 - 6, top - 7, w + 12, 2).fill({ color: COLOR.stoneLit, alpha: 0.55 });
    g.rect(x0 - 6, top - 1, w + 12, 2).fill({ color: 0x140e07, alpha: 0.8 });
    // Machicolations: the boxes a defender drops things through.
    for (let x = x0 - 3; x < x0 + w + 1; x += 11) {
      g.rect(x, top - 1, 5, 3.5).fill({ color: 0x0e0a05, alpha: 0.85 });
    }
    paintCrenellations(g, x0 - 6, top - 7, w + 12, seed);
  }
  paintWeathering(g, x0, w, courses * BLOCK_H, seed + 5);
  paintFaceRamp(g, x0, w, courses * BLOCK_H);
  if (integrity <= WALL_DAMAGED) paintRubble(g, x0, w, seed);
  paintWallBase(g, x0, w, seed);
  // The opening is drawn **last**, over the face shading and over the splayed
  // footing. Drawn with the masonry it took six passes of the face ramp and the
  // whole footing on top of it, so the portcullis faded out about 60px above the
  // threshold and the bottom of the arch was a black band - which is why the
  // gate read as a slot in the wall a row clear of the drawbridge that lands on
  // it, rather than as a way through.
  if (integrity > WALL_DESTROYED) {
    const jamb = 4;
    // The passage: dark at the crown where the vault is, opening to the lit
    // ground of the gate hall at the threshold. A flat black rectangle here is a
    // hole punched in the panel, not a way in.
    // Not black. A flat near-black rectangle in a wall is a hole punched in the
    // panel; the vault behind a portcullis is dark *stone*, and it is kept above
    // the darkest mortar on the masonry outside it (measured at luma 13.6).
    g.rect(-archW / 2, -archH, archW, archH).fill({ color: 0x14100a });
    for (let i = 0; i < 7; i++) {
      const k = i / 7;
      g.rect(-archW / 2 + 2, -archH * 0.42 * (1 - k), archW - 4, archH * 0.42 * (1 - k) + 1)
        .fill({ color: 0x6b5836, alpha: 0.07 });
    }
    g.rect(-archW / 2 + 2, -7, archW - 4, 7).fill({ color: 0x8a7448, alpha: 0.3 });
    // The arch ring: voussoirs round the head of the opening.
    for (let i = 0; i <= 10; i++) {
      const t = i / 10;
      const ax = -archW / 2 + t * archW;
      const ay = -archH + (1 - Math.sin(Math.PI * t)) * archW * 0.22;
      g.rect(ax - 3, ay - 7, 8, 9)
        .fill({ color: mixColor(COLOR.stoneLit, COLOR.stone, 0.2 + noise(seed, i * 3) * 0.4) });
      g.rect(ax - 3, ay - 7, 8, 1.6).fill({ color: 0xcdbd9a, alpha: 0.7 });
    }
    // The jambs, catching the light on their inner reveal.
    g.rect(-archW / 2 - jamb, -archH * 0.78, jamb, archH * 0.78)
      .fill({ color: mixColor(COLOR.stoneLit, COLOR.stone, 0.25) });
    g.rect(archW / 2, -archH * 0.78, jamb, archH * 0.78)
      .fill({ color: mixColor(COLOR.stone, COLOR.stoneShadow, 0.4) });
    if (open) {
      // Raised: the portcullis is a band of teeth tucked under the arch.
      g.rect(-archW / 2, -archH, archW, 7).fill({ color: 0x3a2d1c });
      g.rect(-archW / 2, -archH, archW, 2).fill({ color: 0x6a5636 });
      g.rect(-archW / 2 - 3, -archH + 6, archW + 6, 3).fill({ color: 0x211a10 });
    } else {
      // Down: an iron grille, lit on its left edge and shadowed on the passage
      // floor behind it, so the bars are modelled rather than being a line
      // drawing on a void.
      for (let x = -archW / 2 + 4; x < archW / 2 - 2; x += 8) {
        g.rect(x + 1.5, -archH + 6, 4, archH - 6).fill({ color: 0x090603, alpha: 0.75 });
        g.rect(x, -archH + 4, 4, archH - 4).fill({ color: 0x574427 });
        g.rect(x, -archH + 4, 1.4, archH - 4).fill({ color: 0x9a7c4c, alpha: 0.85 });
        g.rect(x + 3, -archH + 4, 1, archH - 4).fill({ color: 0x1d1509, alpha: 0.8 });
        // A spike at the foot of each bar, which is what a portcullis is.
        g.poly([x, -3, x + 4, -3, x + 2, 3]).fill({ color: 0x3d3020 });
      }
      for (let y = -archH + 12; y < -8; y += 20) {
        g.rect(-archW / 2 + 2, y + 1.5, archW - 4, 4).fill({ color: 0x090603, alpha: 0.7 });
        g.rect(-archW / 2 + 2, y, archW - 4, 4).fill({ color: 0x36363d });
        g.rect(-archW / 2 + 2, y, archW - 4, 1.4).fill({ color: 0x7c7c88, alpha: 0.85 });
      }
    }
    // The arms over the gate. A gatehouse is where a town says whose it is, and
    // this is the one shield on the works a player looks straight at.
    if (livery !== undefined) {
      const sw = Math.min(20, archW * 0.36);
      const sy = -archH - archW * 0.24 - 10;
      const sh = sw * 1.15;
      g.poly([-sw / 2 - 2, sy - 2, sw / 2 + 2, sy - 2, sw / 2 + 2, sy + sh * 0.6, 0, sy + sh + 2,
        -sw / 2 - 2, sy + sh * 0.6]).fill({ color: 0x110c06, alpha: 0.7 });
      g.poly([-sw / 2, sy, sw / 2, sy, sw / 2, sy + sh * 0.58, 0, sy + sh, -sw / 2, sy + sh * 0.58])
        .fill({ color: livery.field });
      g.poly([0, sy, sw / 2, sy, sw / 2, sy + sh * 0.58, 0, sy + sh])
        .fill({ color: 0x000000, alpha: 0.24 });
      g.poly([-sw / 2, sy, sw / 2, sy, sw / 2, sy + sh * 0.58, 0, sy + sh, -sw / 2, sy + sh * 0.58])
        .stroke({ width: 1.6, color: livery.trim, alpha: 0.9, alignment: 0.5 });
      g.rect(-sw / 2 + 2, sy + sh * 0.3, sw - 4, 2).fill({ color: livery.trim, alpha: 0.8 });
    }
  }
}

export function paintTower(
  g: Graphics, w: number, height: number, integrity: number, seed: number, livery?: Livery,
): void {
  const x0 = -w / 2;
  const courses = Math.max(1, Math.round(height / BLOCK_H));
  // Three states, three heights. A tower that lost its top is the clearest
  // reading of "this segment has taken damage" anywhere on the works, and the
  // old code drew a damaged tower exactly like an intact one.
  const standing = integrity >= WALL_INTACT
    ? courses
    : integrity === WALL_DAMAGED
      ? Math.max(2, Math.round(courses * 0.62))
      : Math.max(1, Math.round(courses * 0.3));
  for (let r = 0; r < standing; r++) {
    const y = -(r + 1) * BLOCK_H;
    // A round tower narrows toward the top by a hair, which reads as curvature.
    const shrink = (r / courses) * w * 0.06;
    let x = x0 + shrink;
    const cw = w - shrink * 2;
    while (x < x0 + shrink + cw) {
      const bw = Math.min(BLOCK_W, x0 + shrink + cw - x);
      // A knocked-about tower's top two courses survive in patches, block by
      // block. Skipping whole courses instead left one floating over a gap.
      const broken = integrity < WALL_INTACT && r >= standing - 2
        && noise(seed, r * 17 + Math.round(x)) > 0.48;
      if (!broken) {
        bed(g, x, y, bw);
        course(g, x, y, bw, seed, r, (r & 1) === 1 ? BLOCK_W / 2 : 0, x0 + shrink, cw);
      }
      x += BLOCK_W;
    }
    // A light ramp across the drum, not two dark strips at the edges. A
    // cylinder lit from the upper left has a terminator that walks round it -
    // brightest a third of the way across, falling away to both sides - and two
    // 16%-wide bars at the ends is the shading of a *box* with its corners
    // rubbed off, which is what these turrets read as.
    for (let k = 0; k < 7; k++) {
      const u = (k + 0.5) / 7;
      // 0 at the light line, 1 at either limb; the shaded limb goes darker.
      const away = Math.abs(u - 0.34) / (u < 0.34 ? 0.34 : 0.66);
      g.rect(x0 + shrink + cw * (k / 7), y, cw / 7 + 0.6, BLOCK_H - MORTAR)
        .fill({
          color: u < 0.34 ? STONE_SUN : mixColor(COLOR.stoneShadow, STONE_SKY, 0.35),
          alpha: u < 0.34 ? 0.2 * (1 - away) : 0.42 * away * away,
        });
    }
  }
  const top = -standing * BLOCK_H;
  if (integrity >= WALL_INTACT) {
    // Arrow slit. It is a *slit* - a hand's width of shadow with a splayed
    // reveal - and the light behind it is a hint. Drawn 3px wide and 34px tall
    // at 0.55 of a saturated orange it read as a glowing rod stuck to the face,
    // and it was the most saturated thing anywhere on the works.
    g.rect(-3.5, top + BLOCK_H * 2.4, 7, BLOCK_H * 2.2)
      .fill({ color: mixColor(COLOR.stoneLit, COLOR.stoneShadow, 0.45) });
    g.rect(-2, top + BLOCK_H * 2.5, 4, BLOCK_H * 2).fill({ color: 0x120d07 });
    g.rect(-1, top + BLOCK_H * 2.9, 2, BLOCK_H * 1.1).fill({ color: 0xc98f3e, alpha: 0.34 });
    // An overhanging battlement course, then merlons.
    g.rect(x0 - 4, top - 6, w + 8, 6).fill({ color: COLOR.stone });
    g.rect(x0 - 4, top - 6, w + 8, 1.6).fill({ color: COLOR.stoneLit, alpha: 0.6 });
    g.rect(x0 - 4, top - 1, w + 8, 1.6).fill({ color: COLOR.stoneShadow, alpha: 0.75 });
    paintCrenellations(g, x0 - 4, top - 6, w + 8, seed + 3);
    // A pitched roof over the fighting top. A crenellated stump and a wall
    // segment have the same silhouette from across the field; a roof is what
    // says "tower" at a glance, and it is what the eye counts a fortification's
    // corners by.
    // A **cone**, not a triangle. The eaves of a conical roof over a round
    // tower are an ellipse - you are above them and you see round the front of
    // the drum - and the triangle this replaced had a ruled horizontal eaves
    // line, which is the profile of a gable seen dead on. Three of those in a
    // column was most of why the works read as three flat-fronted blocks.
    // The eaves sit **on the crenellated ring**, not below it, so the merlons
    // still show round the skirt of the roof: that ring is the one silhouette
    // that reads as fortification at any size, and a roof pulled down over it
    // turns the turret into a hut.
    // The eaves inside the parapet, not oversailing it: at w/2 the cone was
    // as wide as the drum and taller than the tower's own visible masonry,
    // and the works read as a row of hats. A reference screenshot's turret
    // roofs sit well inside their crenellated ring.
    const rw = w * 0.36;
    const eave = top - 16;
    const eaveDrop = rw * 0.3;
    // Steep: at 19 over an eaves ellipse 30-45 wide the cone was flatter
    // than its own eaves and read as a green disc laid on the drum.
    // A reference screenshot and a reference screenshot roof their towers at
    // about 1.3x the drum's width.
    const apex = eave - Math.max(16, rw * 1.25);
    // The roof is the town's. In the classic roof colour and the pennants are how
    // a player names the town he is standing in front of, and a grey cone is
    // the same cone for all twelve of them.
    // Laid **as the livery's own colour**, with the shade over it as alpha.
    // Darkening it into the tile itself (`mixColor(roof, 0x14100b, 0.22)`) put
    // the town's colour nowhere in the fills and broke the surface spec's
    // "flies the defending faction's cloth" - which is a real property, not a
    // test artefact: the roof is how a player names the town he is besieging.
    const tile = livery?.roof ?? 0x3a2f26;
    // The soffit under the eaves, which is what an overhanging roof shows to
    // anyone below it.
    g.ellipse(0, eave + 2.5, rw + 3, eaveDrop + 2.5)
      .fill({ color: mixColor(tile, 0x120c07, 0.66) });
    // The cone, built as a fan of slope facets from the elliptical eaves up to
    // the apex, each one shaded by how far its own face has turned away from
    // the light. A cone has no flat side, so a two-tone triangle cannot read as
    // one however it is split.
    const FACETS = 14;
    for (let i = 0; i < FACETS; i++) {
      const a0 = Math.PI * (i / FACETS);
      const a1 = Math.PI * ((i + 1) / FACETS);
      // Angles run left to right across the *front* of the cone.
      const p = (a: number): readonly [number, number] =>
        [-Math.cos(a) * rw, eave + Math.sin(a) * eaveDrop];
      const [x0f, y0f] = p(a0);
      const [x1f, y1f] = p(a1);
      const u = (i + 0.5) / FACETS;
      // Brightest a third of the way from the lit limb, falling to both edges.
      // The tile colour is laid **as itself** and the light goes over it as
      // alpha, so the roof still carries the town's own value rather than a
      // fourteen-way interpolation of it - `battle-siege-surface.spec.ts` holds
      // that, and it is right to: the roof is how a player names the town.
      const lit = 1 - Math.min(1, Math.abs(u - 0.3) / 0.7);
      g.poly([x0f, y0f, x1f, y1f, 0, apex]).fill({ color: tile });
      g.poly([x0f, y0f, x1f, y1f, 0, apex])
        // 0.24, not 0.4: at 0.4 the lit limb of a Greenmantle roof came out a
        // bright grass green and a critic named "bright green cone hats";
        // The expansion's roofs are the town's colour in the wall's own shade.
        .fill({ color: lit > 0.55 ? 0xf6ead0 : 0x0d0a06, alpha: (lit > 0.55 ? 0.24 : 0.4) * Math.abs(lit - 0.55) / 0.55 });
    }
    // The eaves line itself, and the courses of tile running round the cone.
    // The cone sits in the wall's own shade rather than being the brightest
    // object on the works; this is the 0.22 the tile used to be mixed with.
    g.poly([-rw, eave, rw, eave, 0, apex]).fill({ color: 0x14100b, alpha: 0.22 });
    g.ellipse(0, eave, rw, eaveDrop)
      .stroke({ width: 1.2, color: 0x150f0a, alpha: 0.8, alignment: 0.5 });
    for (let i = 1; i < 4; i++) {
      const k = i / 4;
      g.ellipse(0, eave + (apex - eave) * k, rw * (1 - k), eaveDrop * (1 - k))
        .stroke({ width: 0.9, color: 0x120c07, alpha: 0.3, alignment: 0.5 });
    }
    g.circle(0, apex - 3, 2.4).fill({ color: COLOR.giltDark });
    if (livery !== undefined) {
      // A staff and a pennant, which is what the eye counts a town's corners by
      // from the far side of the field.
      g.rect(-0.9, apex - 18, 1.8, 16).fill({ color: 0x2a2118 });
      g.rect(-0.9, apex - 18, 0.9, 16).fill({ color: 0x8a7a5c, alpha: 0.6 });
      g.poly([0.9, apex - 17, 14, apex - 13, 0.9, apex - 9]).fill({ color: livery.field });
      g.poly([0.9, apex - 17, 14, apex - 13, 8, apex - 12.6])
        .fill({ color: mixColor(livery.field, 0xffffff, 0.45), alpha: 0.8 });
      g.poly([0.9, apex - 17, 14, apex - 13, 0.9, apex - 9])
        .stroke({ width: 0.9, color: livery.trim, alpha: 0.75, alignment: 0.5 });
    }
  } else if (integrity === WALL_DAMAGED) {
    // A tower knocked open: the slit is a hole now, and the top has come down.
    g.rect(-6, top + BLOCK_H * 1.6, 12, BLOCK_H * 3.4).fill({ color: 0x0e0a06, alpha: 0.9 });
    paintRubble(g, x0, w, seed + 5);
  } else {
    paintBreach(g, x0, w, height, seed);
  }
  paintWeathering(g, x0, w, standing * BLOCK_H, seed + 11);
  paintFaceRamp(g, x0, w, standing * BLOCK_H);
  paintWallBase(g, x0, w, seed);
}

/**
 * The citadel of a besieged town: a square donjon with a lower wing toward
 * the field, in the same masonry as the curtain.
 *
 * Drawn in the graphic's own space, ground line at y = 0, `w` centred on x =
 * 0. The tower takes the right 0.68 of the width and the full height; the
 * wing takes the left 0.5 at 0.42 of the height and stands in front, so the
 * two overlap by a bay and read as one building. The tower has a battered
 * plinth, string courses every fifth course, two arrow slits, a corbel table
 * under a crenellated crown and a hipped roof in the town's own tile; the
 * wing has a door arch and its own parapet. Every course is `course()` and
 * every parapet `paintCrenellations()`, so the keep and the bays it stands
 * behind are one stone.
 */
export function paintKeep(g: Graphics, w: number, height: number, seed: number, livery: Livery): void {
  const towerW = w * 0.68;
  const towerX0 = w / 2 - towerW;
  const wingW = w * 0.5;
  const wingX0 = -w / 2;
  const wingH = height * 0.42;
  const stone = (x0: number, width: number, courses: number, seedAt: number): void => {
    for (let r = 0; r < courses; r++) {
      const y = -(r + 1) * BLOCK_H;
      let x = x0;
      while (x < x0 + width) {
        const bw = Math.min(BLOCK_W, x0 + width - x);
        bed(g, x, y, bw);
        course(g, x, y, bw, seedAt, r, (r & 1) === 1 ? BLOCK_W / 2 : 0, x0, width);
        x += BLOCK_W;
      }
    }
  };
  // ---- the tower
  const towerCourses = Math.max(4, Math.round(height / BLOCK_H));
  stone(towerX0, towerW, towerCourses, seed);
  // String courses: a slightly proud band every fifth course, lit on top.
  for (let r = 5; r < towerCourses - 2; r += 5) {
    const y = -r * BLOCK_H;
    g.rect(towerX0 - 1, y - 1, towerW + 2, 2.4).fill({ color: STONE_WARM, alpha: 0.55 });
    g.rect(towerX0 - 1, y + 1.4, towerW + 2, 1.6).fill({ color: 0x130d07, alpha: 0.6 });
  }
  // Arrow slits, a hand's width of shadow with a lit sill.
  for (const k of [0.36, 0.64]) {
    const sy = -height * k;
    const sx = towerX0 + towerW * 0.5 - 3;
    g.rect(sx, sy, 6, BLOCK_H * 1.9).fill({ color: 0x0e0a06, alpha: 0.92 });
    g.rect(sx - 1, sy + BLOCK_H * 1.9, 8, 1.6).fill({ color: STONE_SUN, alpha: 0.5 });
  }
  const towerTop = -towerCourses * BLOCK_H;
  // Corbel table under the crown: a row of dark brackets throwing small shadows.
  for (let x = towerX0 + 3; x < towerX0 + towerW - 3; x += 9) {
    g.rect(x, towerTop + 2, 4, 5).fill({ color: 0x130d07, alpha: 0.7 });
    g.rect(x, towerTop + 1, 4, 1.4).fill({ color: STONE_SUN, alpha: 0.35 });
  }
  paintCrenellations(g, towerX0, towerTop, towerW, seed + 3);
  // The hipped roof behind the parapet: a ridge, a lit left slope and a
  // shaded right slope in the town's tile, and a pennant on the ridge.
  const tile = livery.roof;
  const merlonTop = towerTop - Math.max(12, towerW / 6) - 2;
  const eaveY = merlonTop + 3;
  // A third of the height: at a sixth the ridge stood a hand above the
  // merlons and the roof read as a green tip on a crenellated stump.
  const ridgeY = eaveY - height * 0.32;
  const inset = towerW * 0.08;
  g.poly([towerX0 + inset, eaveY, towerX0 + towerW - inset, eaveY,
    towerX0 + towerW * 0.58, ridgeY, towerX0 + towerW * 0.42, ridgeY]).fill({ color: tile });
  g.poly([towerX0 + inset, eaveY, towerX0 + towerW * 0.5, eaveY, towerX0 + towerW * 0.42, ridgeY])
    .fill({ color: 0xf6ead0, alpha: 0.22 });
  g.poly([towerX0 + towerW * 0.5, eaveY, towerX0 + towerW - inset, eaveY, towerX0 + towerW * 0.58, ridgeY])
    .fill({ color: 0x0d0a06, alpha: 0.34 });
  for (let k = 1; k < 4; k++) {
    const t = k / 4;
    g.moveTo(towerX0 + inset + (towerW * 0.42 - inset) * t, eaveY + (ridgeY - eaveY) * t)
      .lineTo(towerX0 + towerW - inset - (towerW * 0.42 - inset) * t, eaveY + (ridgeY - eaveY) * t)
      .stroke({ width: 0.9, color: 0x120c07, alpha: 0.3 });
  }
  g.rect(towerX0 + towerW * 0.42, ridgeY - 1.5, towerW * 0.16, 2.5).fill({ color: mixColor(tile, 0x120c07, 0.5) });
  const px0 = towerX0 + towerW * 0.5;
  g.rect(px0 - 0.9, ridgeY - 20, 1.8, 19).fill({ color: 0x2a2118 });
  g.rect(px0 - 0.9, ridgeY - 20, 0.9, 19).fill({ color: 0x8a7a5c, alpha: 0.6 });
  g.poly([px0 + 0.9, ridgeY - 19, px0 + 16, ridgeY - 14.5, px0 + 0.9, ridgeY - 10]).fill({ color: livery.field });
  g.poly([px0 + 0.9, ridgeY - 19, px0 + 16, ridgeY - 14.5, px0 + 0.9, ridgeY - 10])
    .stroke({ width: 0.9, color: livery.trim, alpha: 0.75, alignment: 0.5 });
  paintWeathering(g, towerX0, towerW, towerCourses * BLOCK_H, seed + 11);
  paintFaceRamp(g, towerX0, towerW, towerCourses * BLOCK_H);
  // ---- the wing, in front
  const wingCourses = Math.max(3, Math.round(wingH / BLOCK_H));
  // Its own shadow on the tower face, so it stands proud rather than painted on.
  g.rect(wingX0 + wingW, -wingCourses * BLOCK_H - 4, 7, wingCourses * BLOCK_H + 4)
    .fill({ color: 0x0b0702, alpha: 0.4 });
  stone(wingX0, wingW, wingCourses, seed + 5);
  const wingTop = -wingCourses * BLOCK_H;
  paintCrenellations(g, wingX0, wingTop, wingW, seed + 7);
  // The door: an arch in the wing's foot with a lit keystone and a dark inside.
  const dw = Math.max(10, wingW * 0.26);
  const dx = wingX0 + wingW * 0.5 - dw / 2;
  const dh = BLOCK_H * 2.4;
  g.rect(dx - 2, -dh, dw + 4, dh).fill({ color: STONE_WARM, alpha: 0.5 });
  g.rect(dx, -dh + 3, dw, dh - 3).fill({ color: 0x0e0a06, alpha: 0.95 });
  g.ellipse(dx + dw / 2, -dh + 3, dw / 2, 5).fill({ color: 0x0e0a06, alpha: 0.95 });
  g.rect(dx + dw / 2 - 2, -dh - 2, 4, 4).fill({ color: STONE_SUN, alpha: 0.6 });
  paintWeathering(g, wingX0, wingW, wingCourses * BLOCK_H, seed + 13);
  paintFaceRamp(g, wingX0, wingW, wingCourses * BLOCK_H);
  paintWallBase(g, wingX0, wingW, seed + 17);
  paintWallBase(g, wingX0 + wingW, towerX0 + towerW - (wingX0 + wingW), seed);
}

/**
 * The dark bed a run of blocks is laid on. It shows only as a 1.5px line round
 * each block and along the two sides of the wall, and that line is the whole
 * difference between courses of masonry and a grey column: without it the
 * blocks butt into each other and into the grass with no edge anywhere.
 */
function bed(g: Graphics, x: number, y: number, w: number): void {
  g.rect(x - 1.2, y - 1.2, w + 2.4, BLOCK_H + 1.2).fill({ color: 0x130d07, alpha: 0.85 });
}

/**
 * Ambient shade banked up the foot of a wall face. A curtain wall is darkest
 * where it meets the ground and lightest at the wall walk; without the ramp the
 * whole face sits at one value and the run reads as a printed pattern.
 */
function paintFaceRamp(g: Graphics, x0: number, w: number, height: number): void {
  for (let i = 0; i < 6; i++) {
    const h = height * (0.44 - i * 0.07);
    if (h <= 0) continue;
    g.rect(x0, -h, w, h).fill({ color: 0x0d0904, alpha: 0.09 });
  }
  g.rect(x0, -height, w, Math.min(6, height)).fill({ color: 0xd8c9a4, alpha: 0.07 });
}

/**
 * What a destroyed segment leaves: a gap the attacker can walk, with the broken
 * stubs of the courses either side of it and the spoil heaped at its foot.
 *
 * The mouth was one flat polygon of `0x080604`. Measured off the 1080p siege
 * shot that is 896px of (8,6,4) with a *median* of L=13.6 - which is exactly the
 * darkest mortar anywhere on the intact masonry (18,13,7). So more than half the
 * breach was at or below the darkest pixel of the wall it is cut out of, and it
 * read as a hole punched in the layer rather than as broken stonework.
 *
 * A real breach is not a void. Three things make it masonry:
 *   - what shows *through* it. The far side of the wall is the lit bailey, so
 *     the opening is bright at the threshold and falls into shade under the
 *     broken soffit, rather than being one value.
 *   - the **reveal**: the wall's own thickness, returning into the opening, lit
 *     on the left return and shaded on the right. It is the single cue that says
 *     the masonry is a body and not a painted panel.
 *   - a torn head. The outline is built out of block ends hanging off the
 *     collapse line, so the edge is courses rather than one drawn curve.
 */
function paintBreach(g: Graphics, x0: number, w: number, height: number, seed: number): void {
  // The jambs stand nearly full height and the hole is between them. Stubs at
  // two thirds put the whole breach *below* the one row pitch of a bay that is
  // ever on screen - a knocked-down bay and an intact one then differed by a few
  // pebbles, which is the opposite of the thing the player is playing against.
  const stub = Math.max(2, Math.round((height * 0.95) / BLOCK_H));
  const gapL = x0 + w * 0.24;
  const gapR = x0 + w * 0.76;
  const gapW = gapR - gapL;
  const cx = (gapL + gapR) / 2;
  // How high the collapse reaches at `t` across the mouth: a sag that is
  // deepest in the middle, because that is where a curtain comes down.
  const arch = height * 0.9;
  const ceiling = (t: number): number => -arch * (0.42 + Math.sin(Math.PI * t) * 0.58);
  // The same curve read the other way: how wide the hole is at height `y`. The
  // interior is filled as horizontal bands, and each band asks this how far it
  // may run, so nothing painted inside the mouth spills past its outline.
  const halfAt = (y: number): number => {
    const sag = (-y / arch - 0.42) / 0.58;
    if (sag <= 0) return gapW / 2;
    if (sag >= 1) return 0;
    return (gapW / 2) * (1 - (2 * Math.asin(sag)) / Math.PI);
  };
  const band = (top: number, bot: number, color: number, alpha = 1): void => {
    const h = halfAt(top);
    if (h <= 0.5) return;
    g.rect(cx - h, top, h * 2, bot - top).fill({ color, alpha });
  };

  // 1. What is behind the wall, seen through the hole. Warm and lit where the
  //    sun reaches the bailey floor at the threshold, falling into vault shade
  //    up under the soffit. Nothing here is darker than the mortar outside.
  const steps = 24;
  for (let i = 0; i < steps; i++) {
    const k = i / steps;
    band(-arch * ((i + 1) / steps), -arch * k,
      mixColor(0x8f7a52, 0x2e251a, Math.min(1, k * 1.7)));
  }
  // Rubble and grit lying on the floor of the opening, and the shadow the head
  // of the breach throws across it.
  for (let i = 0; i < 14; i++) {
    const t = noise(seed, i * 5 + 61);
    const x = cx + (t - 0.5) * gapW * 0.86;
    const y = -noise(seed, i * 5 + 63) * arch * 0.16;
    const r = 2 + noise(seed, i * 5 + 67) * 4.5;
    g.ellipse(x, y, r, r * 0.55).fill({ color: mixColor(0x4a3f2c, 0x8a7a58, noise(seed, i * 7 + 3)) });
    g.ellipse(x, y - r * 0.3, r * 0.7, r * 0.24).fill({ color: 0xbda87c, alpha: 0.3 });
  }
  band(-arch * 0.34, -arch * 0.14, 0x14100a, 0.22);

  // 2. The reveal. Both returns run from the threshold to the height the hole
  //    reaches at the jamb, which is its lowest, so neither can poke through
  //    the outline. Coursed, because a wall's thickness is coursed too.
  const revW = Math.max(3, w * 0.07);
  const revTop = ceiling(0);
  for (const side of [0, 1]) {
    const rx = side === 0 ? gapL : gapR - revW;
    const face = side === 0
      ? mixColor(COLOR.stoneLit, COLOR.stone, 0.4)
      : mixColor(COLOR.stone, COLOR.stoneShadow, 0.45);
    g.rect(rx, revTop, revW, -revTop).fill({ color: face });
    for (let y = revTop; y < -2; y += BLOCK_H) {
      g.rect(rx, y, revW, 1).fill({ color: 0x120c06, alpha: 0.6 });
      g.rect(rx, y + 1, revW, 1).fill({ color: COLOR.stoneLit, alpha: 0.25 });
    }
    // The arris where the reveal turns the corner onto the outer face.
    g.rect(side === 0 ? rx : rx + revW - 1.4, revTop, 1.4, -revTop)
      .fill({ color: side === 0 ? 0xbdae8c : 0x0f0a05, alpha: 0.55 });
  }

  // 3. The torn head: block ends hanging off the collapse line into the mouth.
  const teeth = 12;
  for (let i = 0; i < teeth; i++) {
    const t = (i + 0.5) / teeth;
    const bw = gapW / teeth + 2;
    const bx = gapL + t * gapW - bw / 2;
    const hang = Math.floor(noise(seed, i * 11 + 31) * 2.4);
    for (let r = 0; r < hang; r++) {
      const y = ceiling(t) + r * BLOCK_H;
      if (y > -BLOCK_H) break;
      bed(g, bx, y, bw);
      course(g, bx, y, bw, seed, i + r, (r & 1) === 1 ? BLOCK_W / 2 : 0, x0, w);
    }
    // The shadow the overhang throws on what is behind it.
    g.rect(bx, ceiling(t) + hang * BLOCK_H, bw, 5).fill({ color: 0x120d08, alpha: 0.4 });
  }

  // 4. The jambs: the courses that survived either side of the hole.
  for (const side of [0, 1]) {
    const sw = w * (0.3 + noise(seed, side * 5 + 1) * 0.06);
    const sx = side === 0 ? x0 : x0 + w - sw;
    for (let r = 0; r < stub; r++) {
      // A ragged top: the higher courses survive only in places, and the break
      // slopes down toward the hole the way a collapse does.
      const lean = Math.round((r / stub) * 2.2);
      const rows = stub - Math.floor(noise(seed, side * 13 + r) * 2.6);
      if (r >= rows) continue;
      const y = -(r + 1) * BLOCK_H;
      const inset = side === 0 ? 0 : lean * 4;
      let x = sx + (side === 0 ? 0 : inset);
      const end = sx + sw - (side === 0 ? lean * 4 : 0);
      while (x < end) {
        const bw = Math.min(BLOCK_W, end - x);
        if (bw < 3) break;
        bed(g, x, y, bw);
        course(g, x, y, bw, seed, r, (r & 1) === 1 ? BLOCK_W / 2 : 0, x0, w);
        x += BLOCK_W;
      }
    }
  }
  paintQuoins(g, x0, w, stub * BLOCK_H * 0.7, seed);
  paintRubble(g, x0, w, seed);

  // 5. The spoil the bay came down as, heaped in its own mouth and ramping out
  //    onto the attacker's ground. Without a floor the hole is a doorway.
  const heapH = Math.min(arch * 0.34, BLOCK_H * 2.6);
  g.poly([
    gapL - 8, 4, gapR + 8, 4,
    gapR - gapW * 0.12, -heapH * 0.55, cx + gapW * 0.1, -heapH,
    cx - gapW * 0.14, -heapH * 0.8, gapL + gapW * 0.1, -heapH * 0.4,
  ]).fill({ color: COLOR.stoneDark });
  for (let i = 0; i < 22; i++) {
    const t = noise(seed, i * 5 + 41);
    const x = gapL - 6 + t * (gapW + 12);
    const lift = 1 - Math.abs(t - 0.5) * 1.7;
    const y = -noise(seed, i * 5 + 47) * heapH * Math.max(0.15, lift);
    const r = 3 + noise(seed, i * 5 + 43) * 6;
    g.ellipse(x + 1, y + 1.4, r, r * 0.55).fill({ color: 0x120d08, alpha: 0.42 });
    g.ellipse(x, y, r, r * 0.62)
      .fill({ color: mixColor(COLOR.stoneDark, COLOR.stone, noise(seed, i * 9 + 3)) });
    g.ellipse(x, y - r * 0.25, r * 0.75, r * 0.3).fill({ color: COLOR.stoneLit, alpha: 0.28 });
  }
}

function paintCrenellations(g: Graphics, x0: number, top: number, w: number, seed: number): void {
  // Short close-set teeth: the expansion's parapet is a comb, and at w/6 (13px on a
  // 78px bay, 20 device px) the merlons were the size of the blocks under them
  // used to be, which is the "toy fort" read.
  const merlon = Math.max(7, w / 10);
  const gap = merlon * 0.42;
  // The parapet's shadow, thrown **up** the screen onto the bay behind this one.
  // Light comes from the upper left and slightly in front, so on a run going
  // away from the camera every bay shades the face of the next one back - and
  // that band is the only thing that tells eleven overlapping bays apart. With
  // no shade between them the run measured as one value from top to bottom and
  // three reviews called it a flat grey slab.
  for (let k = 0; k < 8; k++) {
    g.rect(x0 - 3, top - 22 + k * 2.5, w + 6, 2.7)
      .fill({ color: 0x0b0803, alpha: 0.03 + k * 0.032 });
  }
  // The string course: the corbelled ledge that oversails the face and carries
  // the parapet. It runs the width of every bay and every bay is one row pitch
  // from the next, so down the run it is a horizontal beat every 50px - which
  // is the thing that turns an unbroken vertical field of blocks into courses
  // of masonry going away from you.
  g.rect(x0 - 3, top - 4, w + 6, 5).fill({ color: 0x120c06, alpha: 0.9 });
  g.rect(x0 - 3, top - 4, w + 6, 3).fill({ color: mixColor(COLOR.stoneLit, COLOR.stone, 0.3) });
  g.rect(x0 - 3, top - 4, w + 6, 1.2).fill({ color: COLOR.stoneLit, alpha: 0.85 });
  // **The wall walk**: the deck behind the parapet, seen through the crenels and
  // over the top of them. It is the one plane on the whole fortification that
  // faces the sky, so it is the lightest thing on it and it is what says the
  // wall has a thickness rather than being a panel. Drawn before the merlons so
  // they stand in front of it, and offset up the screen along `OBLIQUE`, which
  // is the direction every other roof and ledge in this build recedes in.
  const walk = 8;
  const ox = walk * OBLIQUE_X;
  const oy = walk * OBLIQUE_Y;
  g.poly([x0 - 3, top - 4, x0 + w + 3, top - 4, x0 + w + 3 + ox, top - 4 + oy, x0 - 3 + ox, top - 4 + oy])
    .fill({ color: mixColor(COLOR.stoneLit, STONE_SUN, 0.34) });
  g.poly([x0 - 3 + ox, top - 4 + oy, x0 + w + 3 + ox, top - 4 + oy,
    x0 + w + 3 + ox, top - 5.6 + oy, x0 - 3 + ox, top - 5.6 + oy])
    .fill({ color: 0x1a130b, alpha: 0.7 });
  // The inner parapet on the far side of the walk, so the deck ends in
  // something. Kept two thirds the outer parapet's height: on a wall going away
  // from the camera the far kerb is mostly hidden by the near merlons anyway,
  // and what it is for is to close the top plane.
  g.rect(x0 - 3 + ox, top - 9 + oy, w + 6, 5).fill({ color: mixColor(COLOR.stone, COLOR.stoneShadow, 0.35) });
  g.rect(x0 - 3 + ox, top - 9 + oy, w + 6, 1.2).fill({ color: COLOR.stoneLit, alpha: 0.5 });
  let x = x0;
  let i = 0;
  while (x < x0 + w) {
    const bw = Math.min(merlon, x0 + w - x);
    // Low and close-set. At 16-19px on a bay whose whole visible slice is one
    // 42px row pitch, the merlons stood clear of the string course with the
    // shaded face of the bay behind showing between them, and the run read as a
    // line of crates set on a ledge. The expansion's parapet is a *comb*: short teeth,
    // narrow crenels, sitting hard down on the walk.
    const hgt = 6 + noise(seed, i * 5) * 2;
    // A dark bed under every merlon, and a face lighter than the wall below it.
    // The merlons are the one part of a bay silhouetted against the *next bay
    // back* rather than against sky; drawn in the wall's own value they melted
    // into that bay's face and the run lost its steps entirely.
    g.rect(x - 1.2, top - hgt - 4, bw + 2.4, hgt + 4.5).fill({ color: 0x130d07, alpha: 0.72 });
    g.rect(x, top - hgt, bw, hgt + 1).fill({ color: mixColor(COLOR.stoneLit, COLOR.stone, 0.18) });
    g.rect(x, top - hgt, bw, 1.6).fill({ color: 0xc4b596, alpha: 0.9 });
    g.rect(x + bw - 1.4, top - hgt, 1.4, hgt).fill({ color: COLOR.stoneShadow, alpha: 0.65 });
    // **The cap.** A merlon is a block, and from in front and a little above you
    // see the top of it. Without this every stone on the works is a rectangle
    // facing the camera, which is the definition of an elevation drawing. The
    // cap is the brightest surface on the bay and it shears the same way the
    // walk behind it does.
    const cd = walk * 0.5;
    g.poly([
      x, top - hgt, x + bw, top - hgt,
      x + bw + cd * OBLIQUE_X, top - hgt + cd * OBLIQUE_Y, x + cd * OBLIQUE_X, top - hgt + cd * OBLIQUE_Y,
    ]).fill({ color: mixColor(COLOR.stoneLit, STONE_SUN, 0.46) });
    g.poly([
      x + cd * OBLIQUE_X, top - hgt + cd * OBLIQUE_Y, x + bw + cd * OBLIQUE_X, top - hgt + cd * OBLIQUE_Y,
      x + bw + cd * OBLIQUE_X, top - hgt - 1.3 + cd * OBLIQUE_Y, x + cd * OBLIQUE_X, top - hgt - 1.3 + cd * OBLIQUE_Y,
    ]).fill({ color: 0x171009, alpha: 0.65 });
    // The merlon's own right return, in shade: the second plane that gives it a
    // thickness rather than a width.
    g.poly([
      x + bw, top - hgt, x + bw + cd * OBLIQUE_X, top - hgt + cd * OBLIQUE_Y,
      x + bw + cd * OBLIQUE_X, top + cd * OBLIQUE_Y, x + bw, top,
    ]).fill({ color: mixColor(COLOR.stoneShadow, STONE_SKY, 0.3), alpha: 0.9 });
    x += merlon + gap;
    i++;
  }
}

/**
 * Fallen masonry. It spills **outward**, into the ditch on the attacker's side,
 * and not only at the foot of the bay it came off.
 *
 * Rubble heaped under its own wall is invisible: the bay in front covers every
 * pixel within a row pitch of the ground. A spoil heap thrown out past the edge
 * of the wall line lands on open ground that nothing else draws over, and it is
 * the one part of a broken bay a player can see from anywhere on the field.
 */
export function paintRubble(g: Graphics, x0: number, w: number, seed: number): void {
  const spill = w * 0.62;
  g.ellipse(x0 - spill * 0.35, 3, spill * 0.72, 11).fill({ color: 0x120e09, alpha: 0.4 });
  for (let i = 0; i < 26; i++) {
    const t = noise(seed, i * 3 + 1);
    // Two thirds of it out on the ground to the left, a third at the footing.
    const x = i % 3 === 0 ? x0 + t * w : x0 - t * spill;
    const reach = i % 3 === 0 ? 16 : 12 * (1 - t);
    const y = -noise(seed, i * 3 + 2) * reach;
    const r = 2.5 + noise(seed, i * 3 + 3) * 6;
    g.ellipse(x + 1, y + 1.5, r, r * 0.6).fill({ color: 0x140e08, alpha: 0.45 });
    g.ellipse(x, y, r, r * 0.62).fill({ color: mixColor(COLOR.stoneDark, COLOR.stone, noise(seed, i * 9)) });
    g.ellipse(x, y - r * 0.24, r * 0.8, r * 0.3).fill({ color: COLOR.stoneLit, alpha: 0.26 });
  }
  g.ellipse(x0 + w / 2, 2, w * 0.52, 7).fill({ color: 0x120e09, alpha: 0.35 });
}

/**
 * A wall's own shadow on the ground: a slab thrown down and to the right, the
 * way every block face on it is lit, plus a hard dark seam right under the
 * footing. A contact ellipse is what a creature stands in; a hundred feet of
 * curtain wall casts a shadow with a straight edge, and without one the masonry
 * ended at the turf with no junction at all.
 */
export function paintGroundShadow(g: Graphics, w: number, depth: number, scale = 1): Graphics {
  g.clear();
  const half = w / 2;
  // The umbra: a sheared slab, thrown down and to the right. It is a
  // parallelogram rather than a rectangle because the thing casting it is a
  // hundred feet of wall and the light is oblique - a rectangle is the shadow of
  // a wall lit from dead ahead, which is to say of a wall on a stage.
  g.poly([
    -half, -2, half, -2,
    half + depth * 1.5, depth * 1.7, -half + depth * 1.5, depth * 1.7,
  ]).fill({ color: 0x120c05, alpha: 0.34 });
  g.rect(-half - 2, -3, w + 4, depth * 0.55).fill({ color: 0x0b0702, alpha: 0.42 });
  // The penumbra along the far edge. A cast shadow with two values and a ruled
  // edge measured 35.6 then 37.7 on the shipped shot - a step function, which is
  // what a hard-edged cutout casts. The sun has a disc, so the terminator has a
  // width, and that width grows with the distance from the occluder: eight
  // bands, each fainter and each a little further out along the shear.
  const soft = Math.max(3, depth * 0.9 * scale);
  for (let k = 0; k < 8; k++) {
    const t = k / 8;
    const y0 = depth * 1.7 + t * soft;
    g.poly([
      -half + depth * 1.5, y0, half + depth * 1.5, y0,
      half + depth * 1.5 + soft * 0.5, y0 + soft / 8, -half + depth * 1.5 + soft * 0.5, y0 + soft / 8,
    ]).fill({ color: 0x120c05, alpha: 0.2 * (1 - t) * (1 - t) });
  }
  // Shade banked against the outside of the footing, reaching out past the wall
  // line into the ditch. Everything a bay draws within its own width is covered
  // by the bay in front of it - which is why the works had no visible junction
  // with the ground anywhere along the run - and this strip is outside that.
  for (let k = 0; k < 5; k++) {
    const t = k / 5;
    g.rect(-half - 16 + t * 14, -8 - t * 4, 16 - t * 12, 16 + t * 8)
      .fill({ color: 0x0b0702, alpha: 0.1 + t * 0.16 });
  }
  return g;
}

/**
 * The keep's throw: `paintGroundShadow`'s sheared slab and its soft
 * terminator, without the hard contact rect and the footing bank - the keep
 * stands on open turf in the margin, not in a ditch, so it has no bank to
 * shade and its own base row is the junction with the ground.
 */
export function paintKeepShadow(g: Graphics, w: number, depth: number, scale = 1): Graphics {
  g.clear();
  const half = w / 2;
  g.poly([
    -half + 2, -1, half - 2, -1,
    half + depth * 1.5, depth * 1.7, -half + depth * 1.5, depth * 1.7,
  ]).fill({ color: 0x120c05, alpha: 0.26 });
  const soft = Math.max(3, depth * 0.9 * scale);
  for (let k = 0; k < 8; k++) {
    const t = k / 8;
    const y0 = depth * 1.7 + t * soft;
    g.poly([
      -half + depth * 1.5, y0, half + depth * 1.5, y0,
      half + depth * 1.5 + soft * 0.5, y0 + soft / 8, -half + depth * 1.5 + soft * 0.5, y0 + soft / 8,
    ]).fill({ color: 0x120c05, alpha: 0.16 * (1 - t) * (1 - t) });
  }
  return g;
}

/**
 * Only the footing's own shade from `paintGroundShadow`: the bank against the
 * outside of the plinth, reaching into the ditch. The slab and the penumbra a
 * bay used to throw are cast by `paintRunShadow` for the whole run at once.
 */
export function paintFootShadow(g: Graphics, w: number): Graphics {
  g.clear();
  const half = w / 2;
  for (let k = 0; k < 5; k++) {
    const t = k / 5;
    g.rect(-half - 16 + t * 14, -8 - t * 4, 16 - t * 12, 16 + t * 8)
      .fill({ color: 0x0b0702, alpha: 0.1 + t * 0.16 });
  }
  return g;
}

/** How many bands the bailey shadow's terminator is graded in. */
export const BAILEY_BANDS = 28;
/**
 * How far the curtain's shadow reaches into its own bailey, in arena px at
 * front-row scale, and how dark its umbra is.
 *
 * 30 at 0.22, down from 46 at 0.38. The wall the shadow belongs to used to be
 * a 110-arena-px column of masonry; it is a ribbon now (`build_siege.py`
 * WIN_H2), and a ribbon a pitch and a half tall does not throw a shadow two
 * thirds of a cell wide. A reference screenshot right of the curtain is lit
 * grass with a narrow dark line at the foot and nothing else; the HD dirt
 * siege throws about half a cell. At 46/0.38 a blind reviewer read a "hard
 * dark wedge" between the wall and the defenders.
 */
export const BAILEY_REACH = 20;
export const BAILEY_UMBRA = 0.14;

/**
 * The bands of a graded terminator: `bands` strips between the far edge
 * (`farPts`, a polyline of x,y pairs) and that edge pushed out by `softPts`
 * (one reach per point), fading as `0.3 * (1 - t)^2`.
 *
 * Band k's outer edge and band k+1's inner edge are the SAME floats -
 * `battle-siege-projection.spec.ts` holds that with `toBe`, not a tolerance -
 * because the bands used to be pushed 0.6px over each other and the double
 * coverage was a hatch of 28 slanted lines a pixel apart, repeating with the
 * row pitch, which a critic read at 2x. Fills that share a vertex-exact edge
 * rasterise watertight; the overlap was never closing a gap.
 */
export function terminatorBands(
  farPts: readonly number[], softPts: readonly number[], bands: number,
): { readonly pts: number[]; readonly alpha: number }[] {
  const n = softPts.length;
  const out: { pts: number[]; alpha: number }[] = [];
  const edge = (t: number, r: number): number => farPts[r * 2]! + softPts[r]! * t;
  for (let k = 0; k < bands; k++) {
    const t0 = k / bands;
    const t1 = (k + 1) / bands;
    const pts: number[] = [];
    for (let r = 0; r < n; r++) pts.push(edge(t0, r), farPts[r * 2 + 1]!);
    for (let r = n - 1; r >= 0; r--) pts.push(edge(t1, r), farPts[r * 2 + 1]!);
    out.push({ pts, alpha: 0.3 * (1 - t0) * (1 - t0) });
  }
  return out;
}

/** One bay of a run, in arena coordinates: where it stands and how big it is. */
export interface RunStation {
  readonly row: number;
  /** The wall line's x at this row (`siegeStationX`). */
  readonly x: number;
  /** The row's ground line. */
  readonly y: number;
  /** Half the bay's drawn width. */
  readonly half: number;
  /** How far the wall throws its shadow, in arena px, at this row's scale. */
  readonly depth: number;
  readonly scale: number;
}

/**
 * The cast shadow of a whole run of masonry as one shape: the slab thrown
 * down and to the right of the wall's own foot line, the dark seam under the
 * footing, and a terminator that grades out over a width - the same three
 * parts `paintGroundShadow` draws for one bay, strung along every bay of the
 * run so there is one edge, not eleven.
 *
 * `stations` back to front. The foot line is the run's right-hand edge on the
 * ground; the front bay also throws its own bottom edge, which is the only
 * place a bay's whole width is not covered by the bay in front of it.
 */
export function paintRunShadow(g: Graphics, stations: readonly RunStation[]): Graphics {
  g.clear();
  if (stations.length < 2) return g;
  const front = stations[stations.length - 1]!;
  // The foot line offset by (dx, dy) per station's own depth, back to front.
  const line = (fx: number, fy: number): number[] => {
    const pts: number[] = [];
    for (const s of stations) pts.push(s.x + s.half + s.depth * fx, s.y - 2 + s.depth * fy);
    return pts;
  };
  // A closed strip between two offsets of the foot line, with the front bay's
  // bottom edge closing it on the near end.
  const strip = (ax: number, ay: number, bx: number, by: number): number[] => {
    const a = line(ax, ay);
    const b = line(bx, by);
    const back: number[] = [];
    for (let i = b.length - 2; i >= 0; i -= 2) back.push(b[i]!, b[i + 1]!);
    return [
      ...a,
      front.x - front.half + front.depth * ax, front.y - 2 + front.depth * ay,
      front.x - front.half + front.depth * bx, front.y - 2 + front.depth * by,
      ...back,
    ];
  };
  // The umbra: the foot line to the foot line thrown by the wall's height.
  g.poly(strip(0, 0, 1.5, 1.7)).fill({ color: 0x120c05, alpha: 0.34 });
  // The seam right under the footing.
  g.poly(strip(-0.1, -0.08, 0.45, 0.55)).fill({ color: 0x0b0702, alpha: 0.42 });
  // The penumbra: eight bands out from the thrown edge, each fainter. The
  // width is the front bay's, so the terminator is one gradient down the run
  // rather than a different one per bay.
  const soft = Math.max(3, front.depth * 0.9 * front.scale) / front.depth;
  for (let k = 0; k < 8; k++) {
    const t0 = k / 8;
    const t1 = (k + 1) / 8;
    g.poly(strip(1.5 + soft * 0.5 * t0, 1.7 + soft * t0, 1.5 + soft * 0.5 * t1, 1.7 + soft * t1))
      .fill({ color: 0x120c05, alpha: 0.2 * (1 - t0) * (1 - t0) });
  }
  return g;
}

/** A pier standing proud of the wall face, footing splayed, with a weathered cap. */
function paintButtress(g: Graphics, x0: number, height: number): void {
  const w = 9;
  const x = x0 - w + 2;
  const h = height * 0.82;
  g.rect(x - 1.5, -h - 3, w + 3, h + 5).fill({ color: 0x130d07, alpha: 0.85 });
  g.rect(x, -h, w, h).fill({ color: mixColor(COLOR.stoneLit, COLOR.stoneShadow, 0.4) });
  g.rect(x, -h, 2, h).fill({ color: COLOR.stoneLit, alpha: 0.45 });
  g.rect(x + w - 2.5, -h, 2.5, h).fill({ color: 0x140e07, alpha: 0.5 });
  // The weathering slope at the head, where it dies into the wall face.
  g.poly([x, -h, x + w, -h, x + w, -h - 9]).fill({ color: mixColor(COLOR.stoneLit, COLOR.stoneShadow, 0.28) });
  g.poly([x, -h, x + w, -h - 9, x + w, -h - 11, x, -h - 2])
    .fill({ color: COLOR.stoneLit, alpha: 0.4 });
  // A splayed foot, so it stands on the ground rather than stopping at it.
  g.poly([x - 3, 2, x + w + 2, 2, x + w, -11, x, -11]).fill({ color: COLOR.stoneDark });
  g.rect(x - 3, 0, w + 5, 2).fill({ color: 0x0b0702, alpha: 0.75 });
}

/**
 * The splayed footing, so the wall meets the ground instead of ending at it.
 *
 * This was one `poly` of `COLOR.stoneDark` and one dark line, and the poly is
 * exactly what a blind review measured on the shipped 1080p shot as **a
 * constant 55.0 luma across 120px** at the front of the run - "a flat plinth
 * with zero texture", and the only unbroken flat region left anywhere on the
 * works. A footing is the dirtiest part of a wall and the one part of it a
 * player is closest to, so it carries the most information, not the least:
 *
 *   - the splay is **battered**, so its face turns toward the sky and takes the
 *     light differently along its length rather than being one value;
 *   - it is laid in *plinth courses*, bigger and rougher than the face above;
 *   - the chamfer at its head catches the sun on the left and sky on the right,
 *     the same temperature split every course of the face carries;
 *   - and earth is banked into the angle, which is where the hard contact seam
 *     actually lives.
 */
function paintWallBase(g: Graphics, x0: number, w: number, seed = 0): void {
  const H = 8;
  g.poly([x0 - 5, 2, x0 + w + 5, 2, x0 + w, -H, x0, -H]).fill({ color: COLOR.stoneDark });
  // Plinth courses. Each block spans the batter, so its own two ends are at
  // different x and the whole run of them reads as a slope rather than a band.
  const bw = 11;
  let i = 0;
  for (let x = x0; x < x0 + w; x += bw) {
    const cw = Math.min(bw, x0 + w - x);
    if (cw < 2) break;
    const u = (x + cw / 2 - x0) / Math.max(1, w);
    const v = noise(seed + 71, i * 13);
    const splay = 5 * (1 - u * 0.15);
    let base = mixColor(COLOR.stone, COLOR.stoneShadow, 0.24 + v * 0.5);
    base = mixColor(base, STONE_WARM, 0.3 * (1 - u));
    base = mixColor(base, STONE_SKY, 0.34 * u);
    g.poly([x - splay, 2, x + cw - splay, 2, x + cw, -H, x, -H]).fill({ color: base });
    // The vertical joint between plinth blocks, and the lit arris on the head of
    // each one - a battered course shows its top edge, and that edge is the
    // brightest line at the foot of the wall.
    g.poly([x - splay, 2, x - splay + 1.6, 2, x + 1.6, -H, x, -H])
      .fill({ color: 0x110c06, alpha: 0.55 });
    g.rect(x, -H, cw, 1.6).fill({ color: STONE_SUN, alpha: 0.4 - u * 0.24 });
    i++;
  }
  // The chamfer along the head of the plinth, where the batter meets the face.
  g.rect(x0, -H - 2, w, 2.4).fill({ color: mixColor(COLOR.stoneLit, COLOR.stone, 0.3) });
  g.rect(x0, -H - 2, w, 1).fill({ color: STONE_SUN, alpha: 0.55 });
  g.rect(x0, -H + 0.2, w, 1.4).fill({ color: 0x0f0a05, alpha: 0.6 });
  // Earth and spoil banked into the angle, and the contact seam under it. The
  // seam is drawn on top of the bank, because the darkest line at the foot of a
  // wall is where the two surfaces actually touch.
  for (let k = 0; k < 14; k++) {
    const t = noise(seed + 83, k * 5);
    const x = x0 - 5 + t * (w + 10);
    const r = 2.2 + noise(seed + 83, k * 5 + 1) * 4.4;
    g.ellipse(x, 1.5 - noise(seed + 83, k * 5 + 2) * 3, r, r * 0.5)
      .fill({ color: mixColor(0x3c3324, 0x6d6046, noise(seed + 83, k * 5 + 3)), alpha: 0.8 });
    g.ellipse(x, 0.2 - noise(seed + 83, k * 5 + 2) * 3, r * 0.7, r * 0.2)
      .fill({ color: 0x9c8f6e, alpha: 0.28 });
  }
  g.rect(x0 - 5, 0.6, w + 10, 2).fill({ color: COLOR.stoneShadow, alpha: 0.85 });
  g.rect(x0 - 5, 2.2, w + 10, 1).fill({ color: 0x070502, alpha: 0.5 });
}

// ---------------------------------------------------------------- helpers

/**
 * Which faction is being besieged, read off the defender's own army.
 *
 * `BattleState` has no faction field - the kernel does not need one - but every
 * creature def id is `<faction>.<creature>`, and the defender's stacks are the
 * garrison of the town whose walls these are. Falls back to `neutral`, which
 * has a livery of its own rather than being a hole in the table.
 */
export function defendingFaction(state: BattleState): string {
  for (let i = 0; i < state.stacks.n; i++) {
    if (state.stacks.side[i] !== SIDE_DEFENDER) continue;
    const id = state.stacks.def[i];
    if (id === undefined) continue;
    const dot = String(id).indexOf(".");
    if (dot > 0) return String(id).slice(0, dot);
  }
  return "neutral";
}
